import { CSSProperties, ReactNode, useEffect, useState } from "react";
import { IUserDetail } from "../models/IUserDetail";
import { IUserRoles } from "../models/IUserRoles";
import { getUserRolesByToken } from "../services/auth";
import { changeMyPassword, getMyProfile, updateMyProfile } from "../services/profile";

/* ------------------------------ Yardımcılar ------------------------------ */

const ERROR_TEXT: Record<string, string> = {
  InvalidLogin: "Mevcut şifre yanlış.",
  ServiceUserOldPassAndNewPassSame: "Yeni şifre eskisiyle aynı olamaz.",
  ServiceUserEmailAlreadyExist: "Bu e-posta başka bir hesapta kullanılıyor.",
  ServiceUserTelNoAlreadyExist: "Bu telefon numarası başka bir hesapta kullanılıyor.",
  EmailAddressError: "Geçerli bir e-posta adresi girin.",
};

function apiError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: unknown } };
  const data = e?.response?.data;
  const first = Array.isArray(data) ? data[0] : null;
  if (first?.code && ERROR_TEXT[first.code]) return ERROR_TEXT[first.code];
  if (first?.description) return first.description;
  const errors = (data as { errors?: Record<string, string[]> })?.errors;
  if (errors) {
    const msg = Object.values(errors).flat()[0];
    if (msg) return msg;
  }
  return fallback;
}

const toDateInput = (iso?: string | null) => (iso ? iso.slice(0, 10) : "");
const isOver13 = (date: string) => {
  const d = new Date(date);
  const limit = new Date();
  limit.setFullYear(limit.getFullYear() - 13);
  return d <= limit;
};
const formatBirthDate = (iso?: string | null) => {
  if (!iso || iso.startsWith("0001")) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" });
};

function passwordStrength(pw: string): { score: number; label: string; color: string } {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-zçğıöşü]/.test(pw) && /[A-ZÇĞİÖŞÜ]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9çğıöşüÇĞİÖŞÜ]/.test(pw)) score++;
  if (pw.length < 6) return { score: 0, label: "Çok kısa", color: "#dc2626" };
  if (score <= 1) return { score: 1, label: "Zayıf", color: "#dc2626" };
  if (score <= 2) return { score: 2, label: "Orta", color: "#d97706" };
  if (score <= 3) return { score: 3, label: "İyi", color: "#16a34a" };
  return { score: 4, label: "Güçlü", color: "#059669" };
}

/* ------------------------------ Parçalar ------------------------------ */

const card: CSSProperties = { background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 18, boxShadow: "0 2px 12px rgba(0,0,0,.05)" };
const inputStyle: CSSProperties = { width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 10, border: "1.5px solid #e5e7eb", fontSize: ".9rem", outline: "none", color: "#111827", background: "#fff" };
const labelStyle: CSSProperties = { fontSize: ".74rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 6, display: "flex", alignItems: "center", gap: 6 };

function Field({ icon, label, children, hint }: { icon: string; label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={labelStyle}><i className={`bi ${icon}`} style={{ color: "#6366f1" }}></i>{label}</div>
      {children}
      {hint && <div style={{ fontSize: ".72rem", color: "#9ca3af", marginTop: 5 }}>{hint}</div>}
    </div>
  );
}

function ReadValue({ children, muted }: { children: ReactNode; muted?: boolean }) {
  return (
    <div style={{ padding: "10px 12px", borderRadius: 10, background: "#f8fafc", border: "1.5px solid #f1f5f9", fontSize: ".92rem", fontWeight: 600, color: muted ? "#9ca3af" : "#111827", minHeight: 43, display: "flex", alignItems: "center", gap: 8, overflowWrap: "anywhere" }}>
      {children}
    </div>
  );
}

function VerifiedBadge({ ok }: { ok: boolean }) {
  return (
    <span style={{ marginLeft: "auto", fontSize: ".66rem", fontWeight: 800, padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap", background: ok ? "#dcfce7" : "#fef3c7", color: ok ? "#15803d" : "#b45309" }}>
      <i className={`bi ${ok ? "bi-patch-check-fill" : "bi-exclamation-circle"} me-1`}></i>{ok ? "Doğrulandı" : "Doğrulanmadı"}
    </span>
  );
}

function PasswordInput({ value, onChange, placeholder, autoComplete }: { value: string; onChange: (v: string) => void; placeholder: string; autoComplete: string }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        autoComplete={autoComplete} maxLength={128} style={{ ...inputStyle, paddingRight: 42 }} />
      <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? "Şifreyi gizle" : "Şifreyi göster"}
        style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", border: "none", background: "none", color: "#9ca3af", cursor: "pointer", padding: "6px 8px" }}>
        <i className={`bi ${show ? "bi-eye-slash" : "bi-eye"}`}></i>
      </button>
    </div>
  );
}

function Alert({ kind, children }: { kind: "error" | "success" | "warn"; children: ReactNode }) {
  const s = {
    error: { bg: "#fef2f2", border: "#fecaca", color: "#b91c1c", icon: "bi-exclamation-circle-fill" },
    success: { bg: "#f0fdf4", border: "#bbf7d0", color: "#15803d", icon: "bi-check-circle-fill" },
    warn: { bg: "#fffbeb", border: "#fde68a", color: "#92400e", icon: "bi-exclamation-triangle-fill" },
  }[kind];
  return (
    <div style={{ background: s.bg, border: `1.5px solid ${s.border}`, color: s.color, borderRadius: 10, padding: "9px 12px", fontSize: ".82rem", display: "flex", gap: 8, alignItems: "flex-start", lineHeight: 1.45 }}>
      <i className={`bi ${s.icon}`} style={{ marginTop: 1 }}></i><span>{children}</span>
    </div>
  );
}

const primaryBtn = (disabled: boolean): CSSProperties => ({
  padding: "10px 20px", borderRadius: 10, border: "none", fontWeight: 700, fontSize: ".86rem",
  background: disabled ? "#e5e7eb" : "linear-gradient(135deg, #6366f1, #4f46e5)", color: disabled ? "#9ca3af" : "#fff",
  cursor: disabled ? "not-allowed" : "pointer", display: "inline-flex", alignItems: "center", gap: 7,
  boxShadow: disabled ? "none" : "0 4px 14px rgba(79,70,229,.25)",
});
const ghostBtn: CSSProperties = { padding: "10px 18px", borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 700, fontSize: ".86rem", cursor: "pointer" };

/* -------------------------------- Sayfa -------------------------------- */

export default function ProfilePage() {
  const [profile, setProfile] = useState<IUserDetail | null>(null);
  const [roles, setRoles] = useState<IUserRoles>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Profil düzenleme
  const [editing, setEditing] = useState(false);
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileSuccess, setProfileSuccess] = useState("");

  // Şifre
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  const load = () => {
    setLoading(true);
    setLoadError("");
    getMyProfile()
      .then((res) => setProfile(res.data))
      .catch(() => setLoadError("Profil bilgileri alınamadı."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    getUserRolesByToken().then((res) => setRoles(res.data ?? [])).catch(() => {});
  }, []);

  const startEdit = () => {
    if (!profile) return;
    setPhone(profile.customerTelNo ?? "");
    setEmail(profile.customerEmail ?? "");
    setBirthDate(formatBirthDate(profile.customerBirthDate) ? toDateInput(profile.customerBirthDate) : "");
    setProfileError("");
    setProfileSuccess("");
    setEditing(true);
  };

  const loginEmail = profile?.verification?.verCustomerEmail || profile?.customerEmail || "";
  const emailChanged = !!profile && email.trim() !== (profile.customerEmail ?? "");
  const phoneChanged = !!profile && phone.trim() !== (profile.customerTelNo ?? "");
  const birthChanged = !!profile && birthDate !== (formatBirthDate(profile.customerBirthDate) ? toDateInput(profile.customerBirthDate) : "");
  const anyChange = emailChanged || phoneChanged || birthChanged;

  const saveProfile = async () => {
    if (!profile) return;
    const e = email.trim();
    const p = phone.trim();
    if (emailChanged && (!e.includes("@") || !e.includes(".com"))) { setProfileError("Geçerli bir e-posta adresi girin (ör. ad@ornek.com)."); return; }
    if (phoneChanged && p.replace(/\D/g, "").length < 7) { setProfileError("Geçerli bir telefon numarası girin."); return; }
    if (birthChanged && birthDate && !isOver13(birthDate)) { setProfileError("Doğum tarihi en az 13 yaşında olmanızı gerektirir."); return; }

    setSavingProfile(true);
    setProfileError("");
    try {
      await updateMyProfile(profile.id, {
        customerEmail: emailChanged ? e : undefined,
        customerTelNo: phoneChanged ? p : undefined,
        customerBirthDate: birthChanged && birthDate ? birthDate : undefined,
      });
      setEditing(false);
      setProfileSuccess(emailChanged
        ? `Bilgileriniz güncellendi. Bundan sonra ${e} ile giriş yapacaksınız.`
        : "Bilgileriniz güncellendi.");
      load();
    } catch (err) {
      setProfileError(apiError(err, "Bilgiler güncellenemedi."));
    } finally {
      setSavingProfile(false);
    }
  };

  const strength = passwordStrength(newPassword);
  const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
  const sameAsOld = newPassword.length > 0 && newPassword === oldPassword;
  const canChangePassword = !!oldPassword && newPassword.length >= 6 && newPassword === confirmPassword && !sameAsOld && !savingPassword;

  const submitPassword = async () => {
    if (!canChangePassword || !loginEmail) return;
    setSavingPassword(true);
    setPasswordError("");
    setPasswordSuccess("");
    try {
      await changeMyPassword({ email: loginEmail, oldPassword, newPassword });
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordSuccess("Şifreniz değiştirildi. Bir sonraki girişte yeni şifrenizi kullanın.");
    } catch (err) {
      setPasswordError(apiError(err, "Şifre değiştirilemedi."));
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading && !profile) {
    return <div style={{ textAlign: "center", padding: "80px 0" }}><div className="spinner-border" style={{ color: "#6366f1" }} role="status" /></div>;
  }

  if (loadError || !profile) {
    return (
      <div style={{ padding: 24, maxWidth: 600, margin: "0 auto" }}>
        <Alert kind="error">{loadError || "Profil bulunamadı."}</Alert>
        <button style={{ ...ghostBtn, marginTop: 12 }} onClick={load}><i className="bi bi-arrow-clockwise me-1"></i>Tekrar dene</button>
      </div>
    );
  }

  const fullName = `${profile.customerName ?? ""} ${profile.customerSurName ?? ""}`.trim();
  const initials = fullName.split(" ").filter(Boolean).map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?";
  const roleNames = roles.filter((r) => r.userRoleIsEnabled !== false).map((r) => r.role?.roleName).filter(Boolean);
  const v = profile.verification;

  return (
    <div style={{ padding: "24px 16px", maxWidth: 880, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18 }}>
      {/* Başlık kartı */}
      <div style={{ ...card, overflow: "hidden" }}>
        <div style={{ background: "linear-gradient(135deg, #4f46e5, #7c3aed)", height: 72 }} />
        <div style={{ padding: "0 20px 18px", display: "flex", alignItems: "flex-end", gap: 16, flexWrap: "wrap", marginTop: -36 }}>
          <div style={{
            width: 76, height: 76, borderRadius: "50%", background: "linear-gradient(135deg, #6366f1, #4f46e5)", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.6rem", fontWeight: 800,
            border: "4px solid #fff", boxShadow: "0 6px 18px rgba(79,70,229,.3)", flexShrink: 0,
          }}>
            {initials}
          </div>
          <div style={{ flex: "1 1 200px", minWidth: 0, paddingBottom: 4 }}>
            <div style={{ fontWeight: 800, fontSize: "1.25rem", color: "#111827" }}>{fullName || "İsimsiz kullanıcı"}</div>
            <div style={{ fontSize: ".84rem", color: "#6b7280", overflowWrap: "anywhere" }}>{profile.customerEmail}</div>
          </div>
          {roleNames.length > 0 && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingBottom: 6 }}>
              {roleNames.map((r) => (
                <span key={r} style={{ fontSize: ".72rem", fontWeight: 800, padding: "4px 10px", borderRadius: 20, background: "#eef2ff", color: "#4338ca" }}>
                  <i className="bi bi-shield-check me-1"></i>{r}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Kişisel bilgiler */}
      <div style={{ ...card, padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
          <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827" }}>
            <i className="bi bi-person-vcard-fill me-2" style={{ color: "#6366f1" }}></i>Kişisel Bilgiler
          </div>
          {!editing && (
            <button style={ghostBtn} onClick={startEdit}><i className="bi bi-pencil me-1"></i>Düzenle</button>
          )}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
          <Field icon="bi-person" label="Ad" hint={editing ? "Ad ve soyad buradan değiştirilemiyor." : undefined}>
            <ReadValue muted={editing}>{profile.customerName || "—"}{editing && <i className="bi bi-lock-fill" style={{ marginLeft: "auto", fontSize: ".8rem" }}></i>}</ReadValue>
          </Field>
          <Field icon="bi-person" label="Soyad">
            <ReadValue muted={editing}>{profile.customerSurName || "—"}{editing && <i className="bi bi-lock-fill" style={{ marginLeft: "auto", fontSize: ".8rem" }}></i>}</ReadValue>
          </Field>

          <Field icon="bi-telephone" label="Telefon" hint={editing && phoneChanged ? "Yeni numara doğrulanmamış olarak kaydedilir." : undefined}>
            {editing ? (
              <input type="tel" style={inputStyle} value={phone} maxLength={20} onChange={(e) => setPhone(e.target.value)} placeholder="+905551112233" autoComplete="tel" />
            ) : (
              <ReadValue muted={!profile.customerTelNo}>
                {profile.customerTelNo
                  ? <a href={`tel:${profile.customerTelNo}`} style={{ color: "inherit", textDecoration: "none" }}>{profile.customerTelNo}</a>
                  : "Eklenmemiş"}
                {profile.customerTelNo && v && <VerifiedBadge ok={!!v.customerTelNoVerified} />}
              </ReadValue>
            )}
          </Field>

          <Field icon="bi-envelope" label="E-posta">
            {editing ? (
              <input type="email" style={inputStyle} value={email} maxLength={256} onChange={(e) => setEmail(e.target.value)} placeholder="ad@ornek.com" autoComplete="email" />
            ) : (
              <ReadValue>
                <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{profile.customerEmail || "—"}</span>
                {v && <VerifiedBadge ok={!!v.emailVerified} />}
              </ReadValue>
            )}
          </Field>

          <Field icon="bi-cake2" label="Doğum tarihi">
            {editing ? (
              <input type="date" style={inputStyle} value={birthDate} max={toDateInput(new Date().toISOString())} onChange={(e) => setBirthDate(e.target.value)} />
            ) : (
              <ReadValue muted={!formatBirthDate(profile.customerBirthDate)}>{formatBirthDate(profile.customerBirthDate) || "Eklenmemiş"}</ReadValue>
            )}
          </Field>
        </div>

        {editing && emailChanged && (
          <div style={{ marginTop: 14 }}>
            <Alert kind="warn">E-postanızı değiştirirseniz <b>giriş e-postanız da değişir</b> ve yeni adres doğrulanmamış olarak kaydedilir.</Alert>
          </div>
        )}
        {profileError && <div style={{ marginTop: 14 }}><Alert kind="error">{profileError}</Alert></div>}
        {profileSuccess && !editing && <div style={{ marginTop: 14 }}><Alert kind="success">{profileSuccess}</Alert></div>}

        {editing && (
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <button style={ghostBtn} onClick={() => { setEditing(false); setProfileError(""); }} disabled={savingProfile}>Vazgeç</button>
            <button style={primaryBtn(!anyChange || savingProfile)} disabled={!anyChange || savingProfile} onClick={saveProfile}>
              {savingProfile ? <><span className="spinner-border spinner-border-sm"></span>Kaydediliyor…</> : <><i className="bi bi-check-lg"></i>Kaydet</>}
            </button>
          </div>
        )}
      </div>

      {/* Şifre değiştir */}
      <div style={{ ...card, padding: 20 }}>
        <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827", marginBottom: 4 }}>
          <i className="bi bi-shield-lock-fill me-2" style={{ color: "#6366f1" }}></i>Şifre Değiştir
        </div>
        <div style={{ fontSize: ".8rem", color: "#9ca3af", marginBottom: 16 }}>
          Güvenliğiniz için mevcut şifrenizi girmeniz gerekiyor.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
          <Field icon="bi-key" label="Mevcut şifre">
            <PasswordInput value={oldPassword} onChange={setOldPassword} placeholder="Mevcut şifreniz" autoComplete="current-password" />
          </Field>
          <div className="d-none d-md-block" />
          <Field icon="bi-key-fill" label="Yeni şifre">
            <PasswordInput value={newPassword} onChange={setNewPassword} placeholder="En az 6 karakter" autoComplete="new-password" />
            {newPassword && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: "flex", gap: 4 }}>
                  {[1, 2, 3, 4].map((i) => (
                    <span key={i} style={{ flex: 1, height: 5, borderRadius: 5, background: i <= strength.score ? strength.color : "#e5e7eb", transition: "background .2s" }} />
                  ))}
                </div>
                <div style={{ fontSize: ".72rem", fontWeight: 700, color: strength.color, marginTop: 4 }}>{strength.label}</div>
              </div>
            )}
          </Field>
          <Field icon="bi-check2-square" label="Yeni şifre (tekrar)">
            <PasswordInput value={confirmPassword} onChange={setConfirmPassword} placeholder="Yeni şifreyi tekrar girin" autoComplete="new-password" />
            {mismatch && <div style={{ fontSize: ".74rem", color: "#dc2626", marginTop: 5, fontWeight: 600 }}>Şifreler eşleşmiyor.</div>}
          </Field>
        </div>

        <ul style={{ margin: "14px 0 0", paddingLeft: 0, listStyle: "none", display: "flex", flexWrap: "wrap", gap: "6px 16px", fontSize: ".76rem" }}>
          {[
            [newPassword.length >= 6, "En az 6 karakter"],
            [!!newPassword && newPassword === confirmPassword, "Tekrarı eşleşiyor"],
            [!!newPassword && !sameAsOld, "Eskisinden farklı"],
          ].map(([ok, text]) => (
            <li key={text as string} style={{ color: ok ? "#15803d" : "#9ca3af", fontWeight: 600 }}>
              <i className={`bi ${ok ? "bi-check-circle-fill" : "bi-circle"} me-1`}></i>{text}
            </li>
          ))}
        </ul>

        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <Alert kind="warn">Mevcut şifre <b>5 kez</b> yanlış girilirse hesap <b>15 dakika</b> kilitlenir.</Alert>
          {passwordError && <Alert kind="error">{passwordError}</Alert>}
          {passwordSuccess && <Alert kind="success">{passwordSuccess}</Alert>}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button style={primaryBtn(!canChangePassword)} disabled={!canChangePassword} onClick={submitPassword}>
            {savingPassword ? <><span className="spinner-border spinner-border-sm"></span>Değiştiriliyor…</> : <><i className="bi bi-shield-check"></i>Şifreyi Değiştir</>}
          </button>
        </div>
      </div>
    </div>
  );
}
